import React, { useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useDispatch, useSelector } from 'react-redux';
import {
  sendAiAssistantMessage,
  setCurrentMessage
} from '../../../../redux/slices/lomavisCreator/lomavisCreator';
import OverlayTrigger from 'react-bootstrap/esm/OverlayTrigger';
import { Tooltip } from 'react-bootstrap';

export const MessageBox: React.FC<any> = () => {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const {
    aiAssistant: {
      conversation: { currentMessage, isWaitingForResponse },
      aIQuota: { available_tokens, used_tokens },
        ui: {
          focusMainTextField
        }
    }
  } = useSelector((state: any) => state.lomavisCreator);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [flashBorder, setFlashBorder] = useState(false); // State for flashing border


  const [isMac, setIsMac] = useState(false);

  useEffect(() => {
    setIsMac(navigator.platform.toUpperCase().includes('MAC'));
  }, []);

  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = textareaRef.current.scrollHeight;
      const maxHeight = window.innerHeight * 0.4;

      if (newHeight > maxHeight) {
        textareaRef.current.style.height = `${maxHeight}px`;
        textareaRef.current.style.overflowY = 'auto';
      } else {
        textareaRef.current.style.height = `${newHeight}px`;
        textareaRef.current.style.overflowY = 'hidden';
      }
    }
  };

  const handleSend = () => {
    // check if user has tokens
    if (parseInt(used_tokens) > available_tokens && available_tokens !== null) {
      return;
    }
    if (!isWaitingForResponse && currentMessage.trim()) {
      dispatch(sendAiAssistantMessage() as any);
      if (textareaRef.current) {
        dispatch(setCurrentMessage(''));
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.overflowY = 'hidden';
      }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSend();
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentMessage, isWaitingForResponse]);

  useEffect(() => {
    if (!focusMainTextField || !textareaRef) return;

    textareaRef.current.focus();
    setFlashBorder(true); // Trigger the flash when focusMainTextField is true
    const timer = setTimeout(() => {
      setFlashBorder(false); // Reset current 1 second
    }, 500);
    return () => clearTimeout(timer); // Cleanup the timer on unmount or next effect trigger
  }, [focusMainTextField])

  useEffect(() => {
    autoResize();
    if (!currentMessage && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.overflowY = 'hidden';
    }
  }, [currentMessage]);

  return (
    <div className="p-3" style={{ height: 'auto' }}>
      <div className={`card bg-white border  border-1 p-3 ${flashBorder ? 'border-primary' : 'border-secondary'}`}>
        <textarea
          ref={textareaRef}
          autoFocus={true}
          className="form-control border-0 mb-2 bg-white "
          disabled={
            isWaitingForResponse ||
            (available_tokens !== null && available_tokens <= used_tokens)
          }
          placeholder={t('lomavisCreator.aiAssistant.typeMessage')}
          rows={2}
          style={{
            resize: 'vertical',
            boxShadow: 'none',
            overflowY: 'hidden'
          }}
          value={currentMessage}
          onChange={(e) => dispatch(setCurrentMessage(e.target.value))}
        />

        <div className="d-flex justify-content-between">
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="general-info-tooltip">
                {t('lomavisCreator.aiAssistant.tokensInfo')}
              </Tooltip>
            }
          >
            <div className="d-flex justify-content-center align-items-center">
              <span
                className={
                  parseInt(used_tokens) > available_tokens &&
                  available_tokens !== null
                    ? 'text-warning'
                    : ''
                }
              >
                <i className="fa-duotone fa-solid fa-coin me-2" />
                {used_tokens} /{' '}
                {available_tokens !== null ? available_tokens : '∞'}
              </span>
            </div>
          </OverlayTrigger>
          <button
            className="btn btn-primary btn-sm"
            onClick={handleSend}
            disabled={!currentMessage.trim() || isWaitingForResponse}
          >
            <span>{t('lomavisCreator.aiAssistant.send')}</span>
            <span className="me-1">{isMac ? ' [⌘⏎]' : ' [Ctrl+⏎]'}</span>
            {isWaitingForResponse && (
              <span
                className="spinner-border spinner-border-sm ms-2"
                aria-hidden="true"
              ></span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
